import {
	type Action,
	createActionsHook,
	createContainer,
	createHook,
	createStore,
} from 'react-sweet-state';

import { type UserSearchItem } from '@atlaskit/smart-common';
import { type AutoSuggestionResult } from '@atlassian/dynamic-cards-search-client';

import { type SearchLinkProps } from '../../../common/ui/search-link';
import { type SearchResultCount } from '../../../common/utils/events/dropdown-results-shown';

type PeopleAndTeamsResults = {
	duration: number;
	results: UserSearchItem[];
};

type AutoSuggestionsResults = {
	duration: number;
	results: AutoSuggestionResult[];
};

type SelectedInputFilters = {
	displayText: string;
	id: string;
	onSubmit: () => {};
};

export type TState = {
	autoSuggestionResults: AutoSuggestionsResults;
	dropdownLoading: boolean;
	peopleAndTeamsResults: PeopleAndTeamsResults;
	prefetchedRecentSearchResults: SearchLinkProps[];
	prefetchedRecentViewsResults: SearchLinkProps[] | undefined;
	quickSearchResults: SearchLinkProps[];
	recentSearchResults: SearchLinkProps[];
	recentViewsResults: SearchLinkProps[];
	resultCount: SearchResultCount;
	selectedInputFilters: SelectedInputFilters[];
	showDropdown: boolean;
};

/**
 * This state object stores the results generated/displayed for each result type.
 */
export const initialState = {
	showDropdown: false,
	dropdownLoading: true,
	prefetchedRecentViewsResults: undefined,
	prefetchedRecentSearchResults: [],
	peopleAndTeamsResults: {
		results: [],
		duration: -1,
	},
	recentViewsResults: [],
	autoSuggestionResults: {
		results: [],
		duration: -1,
	},
	quickSearchResults: [],
	recentSearchResults: [],
	resultCount: {
		peopleAndTeams: 0,
		autoSuggestion: 0,
		quickSearch: 0,
		recentSearch: 0,
		recentViews: 0,
	},
	selectedInputFilters: [],
};

const actions = {
	setShowDropdown:
		(showDropdown: boolean): Action<TState> =>
		({ setState }) => {
			setState({ showDropdown });
		},
	setDropdownLoading:
		(dropdownLoading: boolean): Action<TState> =>
		({ setState }) => {
			setState({ dropdownLoading });
		},
	setPeopleAndTeamsResults:
		(peopleAndTeamsResults: PeopleAndTeamsResults): Action<TState> =>
		({ setState }) => {
			setState({ peopleAndTeamsResults });
		},
	setRecentViewsResults:
		(recentViewsResults: SearchLinkProps[]): Action<TState> =>
		({ setState }) => {
			setState({ recentViewsResults });
		},
	setAutoSuggestionResults:
		(autoSuggestionResults: AutoSuggestionsResults): Action<TState> =>
		({ setState }) => {
			setState({ autoSuggestionResults });
		},
	setRecentSearchResults:
		(recentSearchResults: SearchLinkProps[]): Action<TState> =>
		({ setState }) => {
			setState({ recentSearchResults });
		},
	setQuickSearchResults:
		(quickSearchResults: SearchLinkProps[]): Action<TState> =>
		({ setState }) => {
			setState({ quickSearchResults });
		},
	setResultCount:
		(resultCount: SearchResultCount): Action<TState> =>
		({ setState }) => {
			setState({ resultCount });
		},
	setPrefetchedRecentViewsResults:
		(prefetchedRecentViewsResults: SearchLinkProps[]): Action<TState> =>
		({ setState }) => {
			setState({ prefetchedRecentViewsResults });
		},
	setPrefetchedRecentSearchResults:
		(prefetchedRecentSearchResults: SearchLinkProps[]): Action<TState> =>
		({ setState }) => {
			setState({ prefetchedRecentSearchResults });
		},
	setSelectedInputFilters:
		(selectedInputFilters: SelectedInputFilters[]): Action<TState> =>
		({ setState }) => {
			setState({ selectedInputFilters });
		},
};

type TActions = typeof actions;

export const DropdownStoreContainer = createContainer();

// Keep store private and use hooks to access state and actions
const Store = createStore<TState, TActions>({
	containedBy: DropdownStoreContainer,
	initialState,
	actions,
	name: 'dynamic-cards-dropdown',
});

// Hooks

export const useDropdownActions = createActionsHook(Store);

export const useDropdown = createHook(Store, {
	selector: (state) => state.showDropdown,
});

export const useDropdownLoading = createHook(Store, {
	selector: (state) => state.dropdownLoading,
});

export const usePeopleAndTeamsResults = createHook(Store, {
	selector: (state) => state.peopleAndTeamsResults,
});

export const useRecentViewsResults = createHook(Store, {
	selector: (state) => state.recentViewsResults,
});

export const useAutoSuggestionResults = createHook(Store, {
	selector: (state) => state.autoSuggestionResults,
});

export const useRecentSearchResults = createHook(Store, {
	selector: (state) => state.recentSearchResults,
});

export const useQuickSearchResults = createHook(Store, {
	selector: (state) => state.quickSearchResults,
});

export const useResultCount = createHook(Store, {
	selector: (state) => state.resultCount,
});

export const usePrefetchedRecentViewsResults = createHook(Store, {
	selector: (state) => state.prefetchedRecentViewsResults,
});

export const usePrefetchedRecentSearchResults = createHook(Store, {
	selector: (state) => state.prefetchedRecentSearchResults,
});

export const useSelectedInputFilters = createHook(Store, {
	selector: (state) => state.selectedInputFilters,
});
